import React, { useCallback } from 'react';

import debounce from 'debounce-promise';
import _get from 'lodash/get';
import { useForm, useFormState } from 'react-final-form';
import { createMutation } from 'react-query-kit';
import { useDispatch, useSelector } from 'react-redux';

import { EcoBankingAxiosClientAuthedInstance } from 'api';
import endpoints from 'api/CompeonReverseApi/endpoints';
import { InputWithField } from 'components/Input';
import { useFormConfig } from 'config/formConfig/hooks';
import AvailableEmailSymbol from 'modules/AvailableEmail/AvailableEmailSymbol';
import EmailLoginLink from 'modules/AvailableEmail/EmailLoginLink';
import { EMAIL_AVAILABILITY_FIELD_NAME } from 'modules/Inquiry/Form/formFields';
import { getFieldValidator } from 'modules/Inquiry/inquiryFieldValidation/validateField';
import { translations } from 'new/form/common/types';
import { useFieldValidators } from 'shared/hooks/useFieldValidators';
import { setIsEmailAvailable, setIsEmailLoading } from 'store/emailAvailable/actions';
import {
  isEmailAvailableSelector,
  isLoadingEmailAvailabilitySelector,
} from 'store/emailAvailable/selectors';
import { getProcessLane } from 'store/inquiryProcess/selectors';
import { useTranslations } from 'utils/hooks/useTranslations';
import { isEmailRegex } from 'utils/regexes';
import { combineValidators } from 'utils/validators';

const useCombinedValidator = (syncValidate?: (...args: any[]) => any) => {
  // lastEmailValidation is used to keep the last validation result (undefined or syncErrors) in state to avoid calling the validation and endpoint again for the same value
  const [lastEmailValidation, setLastEmailValidation] = React.useState<{
    value: undefined | string;
    lastReturn: any;
  }>({ value: undefined, lastReturn: undefined });
  const dispatch = useDispatch();
  const { change } = useForm();
  const t = useTranslations(
    'pages.companyDetails.sections.contactPerson.fields.companyDetailsEmail.errors.emailTaken',
  );

  const setEmailAvailability = (available: boolean) => {
    dispatch(setIsEmailAvailable(available));
    // Sets a field in the form state to keep the email availability status so the form validation can access it
    change(EMAIL_AVAILABILITY_FIELD_NAME, available);
  };
  const checkEmailMutation = useCheckEmailMutation();
  const checkEmailAvailability = async (email: string | undefined) => {
    if (!email) {
      dispatch(setIsEmailLoading(false));
      setEmailAvailability(true);
      return true;
    }

    try {
      const response = await checkEmailMutation.mutateAsync({ email });
      dispatch(setIsEmailLoading(false));
      if (response.status === 200) {
        setEmailAvailability(false);
        return false;
      }
    } catch (error: any) {
      dispatch(setIsEmailLoading(false));
      if (error.response.status === 404) {
        setEmailAvailability(true);
        return true;
      }
      console.error(error);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const validate = useCallback(
    debounce(async (value: any, allValues: any) => {
      // If the value is the same as the last one, return the last result to avoid calling the endpoint again
      if (value === lastEmailValidation.value && value !== undefined) {
        return lastEmailValidation.lastReturn;
      }
      dispatch(setIsEmailLoading(true));

      // Validates the field with synchronous validators
      const syncErrors = syncValidate ? syncValidate(value, allValues) : undefined;
      if (syncErrors) {
        dispatch(setIsEmailLoading(false));
        setLastEmailValidation({ value, lastReturn: syncErrors });
        return syncErrors;
      }

      // Call the endpoint to check the email availability
      const available = await checkEmailAvailability(value);
      const emailExistsError = t('haveAccount');

      if (available) {
        setLastEmailValidation({ value, lastReturn: undefined });
        return undefined;
      }
      setLastEmailValidation({ value, lastReturn: emailExistsError });
      return emailExistsError;
    }, 200),
    [setIsEmailLoading, lastEmailValidation],
  );

  return { validate };
};

const useCheckEmailMutation = createMutation({
  mutationFn: async (variables: { email: string | undefined }) => {
    return EcoBankingAxiosClientAuthedInstance.post(endpoints.USERS.EMAIL_AVAILABILITY.compose(), {
      email: variables.email,
    });
  },
});

type Props = {
  name: string;
  shouldShowEmailIcon?: boolean;
  useDefaultValidation?: boolean;
};

export const ContactPersonEmail = ({
  name,
  shouldShowEmailIcon = false,
  useDefaultValidation = true,
}: Props) => {
  const { required, isEmail } = useFieldValidators();
  const t = useTranslations();
  const { values } = useFormState();
  const emailValue = values && _get(values, name);
  const isLoading = useSelector(isLoadingEmailAvailabilitySelector);
  const isEmailAvailable = useSelector(isEmailAvailableSelector);
  const processLane = useSelector(getProcessLane);
  const { selectedInquiryType } = useFormConfig();

  const fieldValidator = getFieldValidator(name, selectedInquiryType, processLane);
  const { validate: combinedValidator } = useCombinedValidator(
    combineValidators(required, isEmail),
  );

  const { validate } = useCombinedValidator();
  const newCombinedValidator = (value: any, allValues: any) => {
    // Needs to be tested separately because emailValue is not up to date at this point
    const isEmailValid = isEmailRegex.test(value);
    if (value && isEmailValid) {
      // Checks email availability and sets a field in the form state with the result so the form validation can access it
      validate(value, allValues);
    }
    // Gets the form validation result
    return fieldValidator(value, allValues);
  };

  const isEmailValid = isEmailRegex.test(emailValue);
  const shouldShowLoginLink = !isLoading && !isEmailAvailable && emailValue && isEmailValid;
  const showEmailIcon = !!emailValue && isEmailValid && shouldShowEmailIcon;

  return (
    <div>
      <InputWithField
        name={name}
        validate={useDefaultValidation ? combinedValidator : newCombinedValidator}
        validateFields={[name]}
        type={'email'}
        errorSuffix={shouldShowLoginLink ? <EmailLoginLink email={emailValue} /> : null}
        caption={t(
          translations.pages.companyDetails.sections.contactPerson.fields.companyDetailsEmail
            .caption,
        )}
        sideSymbol={() =>
          showEmailIcon && (
            <AvailableEmailSymbol
              isLoading={isLoading}
              isAllowedToInquiry={combinedValidator(emailValue, values)}
            />
          )
        }
      />
    </div>
  );
};
